import type { ReactElement } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  confirmingLabel?: string;
  isLoading?: boolean;
  variant?: 'default' | 'destructive';
  readOnly?: boolean;
}

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmingLabel,
  isLoading,
  variant = 'destructive',
  readOnly,
}: ConfirmationDialogProps): ReactElement {
  // Generate loading label: remove trailing 'e' before adding 'ing' for better grammar
  const loadingLabel =
    confirmingLabel ??
    (confirmLabel.endsWith('e')
      ? `${confirmLabel.slice(0, -1)}ing...`
      : `${confirmLabel}ing...`);

  // Prevent closing dialog during loading (blocks Escape key)
  const handleOpenChange = (open: boolean): void => {
    // Only handle close events when not loading
    if (!open && !isLoading) {
      onClose();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            onClick={onConfirm}
            disabled={isLoading || (readOnly ?? false)}
            variant={variant}
          >
            {isLoading ? loadingLabel : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

ConfirmationDialog.displayName = 'ConfirmationDialog';
